import { createContext, useContext, useState, useEffect, useCallback } from "react";
import API from "@/lib/api";

const ThemeContext = createContext();

const THEMES = {
  dark: {
    "--background": "222.2 84% 4.9%",
    "--foreground": "210 40% 98%",
    "--card": "222.2 47.4% 11.2%",
    "--card-foreground": "210 40% 98%",
    "--popover": "222.2 47.4% 11.2%",
    "--popover-foreground": "210 40% 98%",
    "--primary": "217.2 91.2% 59.8%",
    "--primary-foreground": "222.2 84% 4.9%",
    "--secondary": "217.2 32.6% 17.5%",
    "--secondary-foreground": "210 40% 98%",
    "--muted": "217.2 32.6% 17.5%",
    "--muted-foreground": "215 20.2% 65.1%",
    "--accent": "217.2 32.6% 17.5%",
    "--accent-foreground": "210 40% 98%",
    "--destructive": "0 84.2% 60.2%",
    "--destructive-foreground": "210 40% 98%",
    "--border": "217.2 32.6% 17.5%",
    "--input": "217.2 32.6% 17.5%",
    "--ring": "217.2 91.2% 59.8%",
    body: "bg-slate-950 text-slate-50",
    label: "dark",
  },
  light: {
    "--background": "0 0% 100%",
    "--foreground": "222.2 84% 4.9%",
    "--card": "0 0% 100%",
    "--card-foreground": "222.2 84% 4.9%",
    "--popover": "0 0% 100%",
    "--popover-foreground": "222.2 84% 4.9%",
    "--primary": "217.2 91.2% 59.8%",
    "--primary-foreground": "0 0% 100%",
    "--secondary": "210 40% 96.1%",
    "--secondary-foreground": "222.2 47.4% 11.2%",
    "--muted": "210 40% 96.1%",
    "--muted-foreground": "215.4 16.3% 46.9%",
    "--accent": "210 40% 96.1%",
    "--accent-foreground": "222.2 47.4% 11.2%",
    "--destructive": "0 84.2% 60.2%",
    "--destructive-foreground": "210 40% 98%",
    "--border": "214.3 31.8% 91.4%",
    "--input": "214.3 31.8% 91.4%",
    "--ring": "217.2 91.2% 59.8%",
    body: "bg-white text-slate-900",
    label: "light",
  },
  colourful: {
    "--background": "260 30% 6%",
    "--foreground": "260 10% 95%",
    "--card": "260 25% 12%",
    "--card-foreground": "260 10% 95%",
    "--popover": "260 25% 12%",
    "--popover-foreground": "260 10% 95%",
    "--primary": "280 80% 60%",
    "--primary-foreground": "260 30% 6%",
    "--secondary": "260 20% 18%",
    "--secondary-foreground": "260 10% 95%",
    "--muted": "260 20% 18%",
    "--muted-foreground": "260 15% 55%",
    "--accent": "330 70% 55%",
    "--accent-foreground": "260 10% 95%",
    "--destructive": "0 84.2% 60.2%",
    "--destructive-foreground": "260 10% 95%",
    "--border": "260 20% 18%",
    "--input": "260 20% 18%",
    "--ring": "280 80% 60%",
    body: "bg-[hsl(260,30%,6%)] text-purple-50",
    label: "colourful",
  },
};

function applyThemeVars(themeName) {
  const vars = THEMES[themeName] || THEMES.dark;
  const root = document.documentElement;
  Object.entries(vars).forEach(([key, value]) => {
    if (key.startsWith("--")) {
      root.style.setProperty(key, value);
    }
  });
  document.body.className = "";
  document.body.classList.add(...(vars.body || "").split(" ").filter(Boolean));
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => localStorage.getItem("mep_theme") || "dark");
  const [source, setSource] = useState("local");
  const [userFetched, setUserFetched] = useState(false);

  // Apply CSS variables whenever theme changes
  useEffect(() => {
    applyThemeVars(theme);
    localStorage.setItem("mep_theme", theme);
  }, [theme]);

  // Exposed method: call ONLY after auth is confirmed (called from AppRoutes)
  const loadUserTheme = useCallback(() => {
    if (userFetched) return; // Only fetch once per session
    API.get("/auth/theme")
      .then(r => {
        const t = r.data.theme || "dark";
        setThemeState(t);
        setSource(r.data.source || "user");
        localStorage.setItem("mep_theme", t);
        setUserFetched(true);
      })
      .catch(() => { setUserFetched(true); });
  }, [userFetched]);

  const setTheme = async (newTheme) => {
    setThemeState(newTheme);
    try {
      await API.put("/auth/theme", { theme: newTheme });
      setSource("user");
    } catch {
      // Still applied locally
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, source, themes: THEMES, loadUserTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}

export { THEMES };
